import Handler from "./Handler";

// The notification console (Notification Centre page). Every call returns
// { ok, data, message } so a page never has to look inside the response envelope.
const BASE = "/admin/notification-console";

const wrap = (response) => {
    if (response?.success) return { ok: true, data: response?.data?.data || {}, message: response?.data?.message || "" };
    return { ok: false, data: null, message: response?.data?.message || "Could not do that. Try again." };
};

const call = async (config) => wrap(await Handler({ ...config, silent: true }));

export const fetchEvents = () => call({ method: "get", url: `${BASE}/events` });
export const saveEventSwitch = (payload) => call({ method: "put", url: `${BASE}/events`, data: payload });
export const saveGroupSwitch = (payload) => call({ method: "put", url: `${BASE}/events/group`, data: payload });
export const saveWording = (payload) => call({ method: "put", url: `${BASE}/templates`, data: payload });
export const previewWording = (payload) => call({ method: "post", url: `${BASE}/preview`, data: payload });
export const sendTest = (payload) => call({ method: "post", url: `${BASE}/test-send`, data: payload });

// Messages an admin writes and sends to staff. `audience` is { mode: ids|hub|role|all, staffIds, hubId, roleId }.
export const previewStaffMessage = (audience) => call({ method: "post", url: `${BASE}/staff-message/preview`, data: audience });
export const sendStaffMessage = (payload) => call({ method: "post", url: `${BASE}/staff-message/send`, data: payload });

export const fetchDeliveries = (params) => call({ method: "get", url: `${BASE}/deliveries`, params });
export const fetchDelivery = (deliveryId) => call({ method: "get", url: `${BASE}/deliveries/${deliveryId}` });
export const retryDelivery = (deliveryId) => call({ method: "post", url: `${BASE}/deliveries/${deliveryId}/retry` });
export const resendDelivery = (deliveryId) => call({ method: "post", url: `${BASE}/deliveries/${deliveryId}/resend` });
export const exportDeliveries = (params) => call({ method: "get", url: `${BASE}/deliveries/export`, params });

export const diagnosePerson = (params) => call({ method: "get", url: `${BASE}/diagnose`, params });
export const fetchHealth = () => call({ method: "get", url: `${BASE}/health` });

export const fetchWhatsapp = () => call({ method: "get", url: `${BASE}/whatsapp` });
export const setWhatsappPaused = (paused) => call({ method: "put", url: `${BASE}/whatsapp/pause`, data: { paused } });
export const syncWhatsappTemplates = () => call({ method: "post", url: `${BASE}/whatsapp/sync-templates` });

// Templates (Phase 6.7) and languages (6.6).
export const fetchTemplateGrid = () => call({ method: "get", url: `${BASE}/tpl/grid` });

// The one template editor (R3): target is { kind: "library", libraryId?, channel? }, { kind: "event",
// eventKey, audience, channel, language } or { kind: "campaign", campaignId, channel, language }.
export const openEditor = (target) => call({ method: "get", url: `${BASE}/editor/open`, params: target });
export const previewEditor = (payload) => call({ method: "post", url: `${BASE}/editor/preview`, data: payload });
export const saveEditor = (payload) => call({ method: "put", url: `${BASE}/editor/save`, data: payload });
export const testEditor = (payload) => call({ method: "post", url: `${BASE}/editor/test`, data: payload });
export const resetEditor = (target) => call({ method: "post", url: `${BASE}/editor/reset`, data: { target } });
export const shareToLibrary = (payload) => call({ method: "post", url: `${BASE}/editor/share`, data: payload });
export const fetchLanguages = () => call({ method: "get", url: `${BASE}/languages` });
export const saveLanguage = (payload) => call({ method: "put", url: `${BASE}/languages`, data: payload });

// Template library: push, email, WhatsApp and SMS templates. Opening, saving and testing one goes through
// the editor calls above; these are the Library's own actions.
export const fetchLibraryMeta = () => call({ method: "get", url: `${BASE}/lib/meta` });
export const fetchLibrary = (params) => call({ method: "get", url: `${BASE}/lib`, params });
export const previewLibraryItem = (payload) => call({ method: "post", url: `${BASE}/lib/preview`, data: payload });
export const submitLibraryItem = (libraryId) => call({ method: "post", url: `${BASE}/lib/submit`, data: { libraryId } });
export const refreshLibrary = () => call({ method: "post", url: `${BASE}/lib/refresh` });
export const duplicateLibraryItem = (libraryId) => call({ method: "post", url: `${BASE}/lib/duplicate`, data: { libraryId } });
export const archiveLibraryItem = (libraryId, archived) => call({ method: "post", url: `${BASE}/lib/archive`, data: { libraryId, archived } });
export const deleteLibraryItem = (libraryId) => call({ method: "post", url: `${BASE}/lib/delete`, data: { libraryId } });
export const revertLibraryItem = (libraryId, version) => call({ method: "post", url: `${BASE}/lib/revert`, data: { libraryId, version } });
export const fetchEmailBrand = () => call({ method: "get", url: `${BASE}/lib/brand` });
export const saveEmailBrand = (payload) => call({ method: "put", url: `${BASE}/lib/brand`, data: payload });

// Media (R1): images, videos and PDFs used by templates. Uploads go as multipart.
const mediaForm = (fields, file) => {
    const form = new FormData();
    Object.entries(fields).forEach(([key, value]) => { if (value !== undefined && value !== null && value !== "") form.append(key, value); });
    form.append("file", file);
    return form;
};
export const fetchMedia = (params) => call({ method: "get", url: `${BASE}/media`, params });
export const fetchMediaDetails = () => call({ method: "get", url: `${BASE}/media/details` });
export const fetchMediaItem = (mediaId) => call({ method: "get", url: `${BASE}/media/item`, params: { mediaId } });
export const uploadMedia = (file, fields = {}) => call({ method: "post", url: `${BASE}/media`, data: mediaForm(fields, file), formdata: true });
export const saveMediaItem = (payload) => call({ method: "put", url: `${BASE}/media/item`, data: payload });
export const replaceMedia = (mediaId, file) => call({ method: "post", url: `${BASE}/media/replace`, data: mediaForm({ mediaId }, file), formdata: true });
export const deleteMedia = (mediaId) => call({ method: "post", url: `${BASE}/media/delete`, data: { mediaId } });

// Saves text as a file in the browser (the delivery log export).
export const downloadText = (filename, text, type = "text/csv;charset=utf-8") => {
    const url = URL.createObjectURL(new Blob([text], { type }));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// The Notification Centre's Overview and Setup pages (R6).
export const fetchOverview = () => call({ method: "get", url: `${BASE}/overview` });
export const fetchEmailSetup = () => call({ method: "get", url: `${BASE}/setup/email` });
export const fetchSmsSetup = () => call({ method: "get", url: `${BASE}/setup/sms` });
